import { useEffect, useState } from 'react';

import { ScreenReaderOnly, Typography } from '@capsule/consumer-design-system';

import { css } from 'styled-components';

import { TransparentButton } from '../TransparentButton';

import { MinusSVG, PlusSVG } from './PlusMinus';
import { PlusMinusButtonContainer } from './styles';

interface PlusMinusButtonProps {
  className?: string;
  initialQuantity: number;
  minQuantity: number;
  maxQuantity: number;
  onChange: (quantity: number) => void;
  onDecrease?: () => void;
  onIncrease?: () => void;
  otcName: string;
  disabled?: boolean;
}

const PlusMinusButton = ({
  className,
  initialQuantity,
  minQuantity,
  maxQuantity,
  onChange,
  onDecrease,
  onIncrease,
  otcName,
  disabled = false,
}: PlusMinusButtonProps) => {
  let calculatedInitialQuantity = initialQuantity;
  if (!calculatedInitialQuantity || calculatedInitialQuantity < minQuantity) calculatedInitialQuantity = 0;
  if (calculatedInitialQuantity && maxQuantity && calculatedInitialQuantity > maxQuantity)
    calculatedInitialQuantity = maxQuantity;

  const [quantity, setQuantity] = useState(calculatedInitialQuantity);
  const [plusDisabled, setPlusDisabled] = useState(false);
  const [minusDisabled, setMinusDisabled] = useState(false);

  useEffect(() => {
    if (disabled) {
      setMinusDisabled(true);
      setPlusDisabled(true);
    } else {
      if (quantity <= minQuantity) {
        setQuantity(minQuantity);
        setMinusDisabled(true);
      } else {
        setMinusDisabled(false);
      }

      if (maxQuantity && quantity >= maxQuantity) {
        setQuantity(maxQuantity);
        setPlusDisabled(true);
      } else {
        setPlusDisabled(false);
      }
    }
  }, [disabled, maxQuantity, quantity]);

  const changeCount = (quantity: number, disabled: boolean, onChange: any) => {
    if (disabled) {
      return;
    }

    setQuantity(quantity);
    onChange(quantity);
  };

  return (
    <PlusMinusButtonContainer aria-live={'polite'} className={className}>
      <TransparentButton
        onClick={() => {
          changeCount(quantity - 1, minusDisabled, onChange);
          if (onDecrease) {
            onDecrease();
          }
        }}
        disabled={minusDisabled}
        aria-disabled={minusDisabled}
        role="button"
      >
        <ScreenReaderOnly>Decrease the quantity by 1 for {otcName}</ScreenReaderOnly>
        <MinusSVG disabled={minusDisabled} />
      </TransparentButton>

      <Typography
        variant="body"
        css={css`
          font-variant-numeric: tabular-nums;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: ${({ theme }) => theme.space.s1};

          /* TODO: Remove this after fixing web fonts and let font-variant-numeric take over */
          min-width: ${({ theme }) => theme.space.s5};
        `}
      >
        {quantity}
      </Typography>

      <TransparentButton
        onClick={() => {
          changeCount(quantity + 1, plusDisabled, onChange);
          if (onIncrease) {
            onIncrease();
          }
        }}
        disabled={plusDisabled}
        aria-disabled={plusDisabled}
        role="button"
      >
        <ScreenReaderOnly>Increase the quantity by 1 for {otcName}</ScreenReaderOnly>
        <PlusSVG disabled={plusDisabled} />
      </TransparentButton>
    </PlusMinusButtonContainer>
  );
};

export default PlusMinusButton;
